import { computed } from 'vue';
import { useTraits, useAvailability } from '@common-vue/media-card-large';
import { useInitSize } from '@common-vue/media-card-large/src/modules/size';
import { useProductType } from '@composables/useProductType';
import { useBrand } from '@composables/useBrand';
import { toTraitId } from '../utils/utils';

/**
 *
 * @param {Size} size
 * @param {Record<string, boolean>} availability
 * @returns {BaseSize}
 */
function toBaseSize(size, availability) {
  return {
    id: size.id,
    name: size.name,
    displayName: size.displayName,
    available: availability[size.id],
  };
}

/**
 * @param {string} id
 * @param {string[]} latchkeyNames
 * @param {Record<string, ParsedSizes>} latchkeySizeMap
 */
function useInitEachSize(id, latchkeyNames, latchkeySizeMap) {
  latchkeyNames.forEach((latchkeyName) => {
    const sizes = latchkeySizeMap[latchkeyName];
    useInitSize({ id: toTraitId(id, latchkeyName), data: sizes });
  });
}

/**
 * @param {string} id
 * @param {import('vue').DeepReadonly<import('vue').Ref<LatchkeyName>>} selectedLatchkey
 * @param {ParsedLatchkeys} parsedLatchkeyData
 * @returns {{
 *  selectedSizeId: SelectedSizeId,
 *  selectedSize: import('vue').WritableComputedRef<Size | null>
 *  sizeProps: import('vue').ComputedRef<SizeProps>
 *  sizes: import('vue').ComputedRef<Size[]>
 * }}
 */
export default function useLatchkeySize(id, selectedLatchkey, { latchkeyNames, latchkeySizeMap }) {
  const { isMcom } = useBrand();
  const { isConsolidatedLatchKey } = useProductType();

  if (isConsolidatedLatchKey) {
    useInitEachSize(id, latchkeyNames, latchkeySizeMap);

    const traitId = computed(() => toTraitId(id, selectedLatchkey.value));

    /**
     * @type {Record<string, { traits: ReturnType<typeof useTraits>, availability: ReturnType<typeof useAvailability>}>}
     */
    const sizesTraits = Object.keys(latchkeySizeMap).reduce((acc, latchkey) => {
      const tId = toTraitId(id, latchkey);
      acc[tId] = {
        traits: useTraits(tId),
        availability: useAvailability(tId),
      };
      return acc;
    }, {});

    const selectedSize = computed({
      get() {
        return sizesTraits[traitId.value]?.traits.selectedSize.value ?? null;
      },
      set(newValue) {
        sizesTraits[traitId.value].traits.selectedSize.value = newValue;
      },
    });

    const selectedSizeId = computed({
      get() {
        return selectedSize.value?.id ?? null;
      },
      set(newValue) {
        sizesTraits[traitId.value].traits.selectedSizeId.value = newValue;
      },
    });

    const availability = computed(() => sizesTraits[traitId.value]?.availability.size.value);

    const sizeArray = computed(() => {
      const sizeData = latchkeySizeMap[selectedLatchkey.value || ''] || null;
      return sizeData?.sizeArray || [];
    });

    const sizes = computed(() => {
      const sizeData = latchkeySizeMap[selectedLatchkey.value || ''] || null;
      return sizeData?.sizeArray.map((size) => toBaseSize(size, availability.value)) || [];
    });

    return {
      sizes: sizeArray,
      selectedSize,
      selectedSizeId,
      sizeProps: computed(() => ({
        latchkeys: latchkeyNames,
        sizes: sizes.value,
        isSizeDropdown: true,
        // dropdownOptional default value -> true
        ...(isMcom && { dropdownOptional: false }),
      })),
    };
  }

  return {
    sizes: computed(() => []),
    selectedSize: computed(() => (null)),
    selectedSizeId: computed(() => (null)),
    sizeProps: computed(() => ({

      latchkeys: [],
      sizes: [],
      isSizeDropdown: false,
    })),
  };
}
